import React, { useEffect, useState } from 'react';
import Component from '../../components/Component';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Alert,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import { createQuickLink, getQuickLinkById, getQuickLinks, updateQuickLink } from '../../services/quickLinksApi';

const defaultForm = {
  title: '',
  image_url: '',
  navigation_path: '',
  priority_order: '',
  is_enabled: true,
  navigation_type: 'internal',
  navigation_params: '{}',
};

const getNavigationPath = (item) => {
  if (!item) return '';
  return (
    item.navigation_path ||
    item.navigation?.path ||
    item.redirection_url ||
    item.redirect_url ||
    item.link ||
    ''
  );
};

const stringifyParams = (params) => {
  if (!params || (typeof params === 'object' && Object.keys(params).length === 0)) {
    return '{}';
  }
  if (typeof params === 'string') return params;
  try {
    return JSON.stringify(params, null, 2);
  } catch (error) {
    return '{}';
  }
};

function QuickLinks() {
  const [quickLinks, setQuickLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(defaultForm);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchQuickLinks();
  }, []);

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchQuickLinks = async () => {
    setLoading(true);
    try {
      const response = await getQuickLinks(50, 0);
      if (response.statusCode === '10000') {
        setQuickLinks(response.data?.quickLinks || response.data || []);
      } else if (response.statusCode === '10003') {
        showSnackbar('Session expired. Please login again.', 'error');
        setTimeout(() => {
          window.location = '/login';
        }, 2000);
      } else {
        setQuickLinks([]);
        showSnackbar(response.message || 'Error fetching quick links', 'error');
      }
    } catch (error) {
      setQuickLinks([]);
      showSnackbar('Error fetching quick links', 'error');
    } finally {
      setLoading(false);
    }
  };

  const populateForm = (item) => {
    setFormData({
      title: item.title || '',
      image_url: item.image_url || '',
      navigation_path: getNavigationPath(item),
      priority_order: item.priority_order ?? '',
      is_enabled: item.is_enabled !== undefined ? item.is_enabled : true,
      navigation_type: item.navigation_type || item.navigation?.type || 'internal',
      navigation_params: stringifyParams(item.navigation_params || item.navigation?.params),
    });
  };

  const handleOpenDialog = async (item = null) => {
    if (item) {
      setEditingItem(item);
      populateForm(item);
      setOpenDialog(true);
      try {
        const response = await getQuickLinkById(item.id);
        if (response.statusCode === '10000') {
          const row = response.data?.quickLink || response.data || item;
          setEditingItem(row);
          populateForm(row);
        }
      } catch (error) {
        // Keep list-row values if the single-item fetch fails
      }
    } else {
      setEditingItem(null);
      setFormData({ ...defaultForm });
      setOpenDialog(true);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setFormData({ ...defaultForm });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (e) => {
    setFormData((prev) => ({ ...prev, is_enabled: e.target.checked }));
  };

  const buildPayload = () => {
    const title = formData.title.trim();
    const imageUrl = formData.image_url.trim();
    const navigationPath = formData.navigation_path.trim();

    if (!title) {
      throw new Error('Title is required');
    }
    if (title.length > 64) {
      throw new Error('Title must be 64 characters or fewer');
    }
    if (!imageUrl) {
      throw new Error('Image URL is required');
    }
    if (!navigationPath) {
      throw new Error('Navigation path is required');
    }
    if (formData.priority_order === '' || Number.isNaN(Number(formData.priority_order))) {
      throw new Error('Priority order is required');
    }

    let navigationParams = {};
    try {
      const parsed = JSON.parse(formData.navigation_params || '{}');
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('Navigation params must be a JSON object');
      }
      navigationParams = parsed;
    } catch (error) {
      if (error.message === 'Navigation params must be a JSON object') throw error;
      throw new Error('Navigation params must be valid JSON');
    }

    return {
      title,
      image_url: imageUrl,
      navigation_path: navigationPath,
      priority_order: Number(formData.priority_order),
      is_enabled: !!formData.is_enabled,
      navigation_type: formData.navigation_type || 'internal',
      navigation_params: navigationParams,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let payload;
    try {
      payload = buildPayload();
    } catch (error) {
      showSnackbar(error.message, 'error');
      return;
    }

    setSaving(true);
    try {
      const response = editingItem
        ? await updateQuickLink(editingItem.id, payload)
        : await createQuickLink(payload);

      if (response.statusCode === '10000') {
        showSnackbar(
          editingItem
            ? 'Quick link updated. Homepage cache can take up to ~60s to refresh.'
            : 'Quick link created. Homepage cache can take up to ~60s to refresh.',
          'success'
        );
        handleCloseDialog();
        fetchQuickLinks();
      } else if (response.statusCode === '10003') {
        showSnackbar('Session expired. Please login again.', 'error');
        setTimeout(() => {
          window.location = '/login';
        }, 2000);
      } else {
        showSnackbar(response.message || 'Error saving quick link', 'error');
      }
    } catch (error) {
      showSnackbar('Error saving quick link', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Component>
        <div className="page-body">
          <div className="container-fluid">
            <div className="page-header">
              <div className="row">
                <div className="col-sm-6">
                  <h3>Quick Links</h3>
                </div>
                <div className="col-sm-6">
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleOpenDialog()}
                    sx={{ float: 'right' }}
                  >
                    Add Quick Link
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="container-fluid">
            <div className="row">
              <div className="col-sm-12">
                <div className="card">
                  <div className="card-body">
                    {loading && !quickLinks.length ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <TableContainer component={Paper}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>ID</strong></TableCell>
                              <TableCell><strong>Title</strong></TableCell>
                              <TableCell><strong>Image</strong></TableCell>
                              <TableCell><strong>Navigation path</strong></TableCell>
                              <TableCell><strong>Priority</strong></TableCell>
                              <TableCell><strong>Enabled</strong></TableCell>
                              <TableCell><strong>Actions</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {!quickLinks.length ? (
                              <TableRow>
                                <TableCell colSpan={7} align="center">
                                  <Typography variant="body1" color="text.secondary">
                                    No quick links found
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              quickLinks.map((item) => (
                                <TableRow key={item.id}>
                                  <TableCell>{item.id}</TableCell>
                                  <TableCell>{item.title || '-'}</TableCell>
                                  <TableCell>
                                    {item.image_url ? (
                                      <img
                                        src={item.image_url}
                                        alt={item.title || ''}
                                        style={{ width: 50, height: 50, objectFit: 'cover' }}
                                      />
                                    ) : '-'}
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                      {getNavigationPath(item) || '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>{item.priority_order ?? '-'}</TableCell>
                                  <TableCell>{item.is_enabled ? 'Yes' : 'No'}</TableCell>
                                  <TableCell>
                                    <IconButton color="primary" size="small" onClick={() => handleOpenDialog(item)}>
                                      <Edit />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{editingItem ? 'Edit Quick Link' : 'Add Quick Link'}</DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="title"
                    label="Title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    inputProps={{ maxLength: 64 }}
                    helperText={`${formData.title.length}/64`}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="image_url"
                    label="Image URL"
                    value={formData.image_url}
                    onChange={handleInputChange}
                    required
                    placeholder="https://…"
                    helperText="HTTPS icon URL"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="navigation_path"
                    label="Navigation path"
                    value={formData.navigation_path}
                    onChange={handleInputChange}
                    required
                    placeholder="/arcade   or   /(tabs)/reels   or   dugout://arcade"
                    helperText="In-app route, deeplink, or https:// URL. This is where the tile opens."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="priority_order"
                    label="Priority order"
                    type="number"
                    value={formData.priority_order}
                    onChange={handleInputChange}
                    required
                    helperText="Unique integer. Lower = shown first. Home grid uses the top 6 enabled."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="navigation-type-label">Navigation type</InputLabel>
                    <Select
                      labelId="navigation-type-label"
                      name="navigation_type"
                      value={formData.navigation_type}
                      label="Navigation type"
                      onChange={handleInputChange}
                    >
                      <MenuItem value="internal">internal</MenuItem>
                      <MenuItem value="external">external</MenuItem>
                      <MenuItem value="web">web</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="navigation_params"
                    label="Navigation params"
                    value={formData.navigation_params}
                    onChange={handleInputChange}
                    multiline
                    minRows={3}
                    placeholder="{}"
                    helperText='JSON object, merged onto the path as query string. Example: { "focus": "fanwars" }'
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Switch checked={formData.is_enabled} onChange={handleSwitchChange} />}
                    label="Enabled"
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={saving}>
                {saving ? <CircularProgress size={20} /> : editingItem ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          <Alert
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Component>
    </div>
  );
}

export default QuickLinks;
